
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, Plus, Tag, X } from "lucide-react";
import { toast } from "@/lib/toast";
import { KanbanBoardTag, KanbanCardTag } from "@/types/database";

interface BoardTagSelectorProps {
  boardId: string;
  cardId: string;
  selectedTags: KanbanCardTag[];
  onTagAdded: (tag: KanbanCardTag) => void;
  onTagRemoved: (tagId: string) => void;
}

export function BoardTagSelector({
  boardId,
  cardId,
  selectedTags,
  onTagAdded,
  onTagRemoved
}: BoardTagSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [selectedColor, setSelectedColor] = useState("#0448FF");
  const [isCreatingNewTag, setIsCreatingNewTag] = useState(false);
  const queryClient = useQueryClient();

  const colors = [
    "#0448FF", "#04A3FF", "#04FFB0", "#13C289", "#F43F5E", 
    "#FB923C", "#FBBF24", "#A855F7", "#EC4899", "#64748B",
    "#9b87f5", "#7E69AB", "#6E59A5", "#8B5CF6", "#D946EF"
  ];

  // Buscar tags disponíveis do quadro
  const { data: boardTags = [] } = useQuery({
    queryKey: ["board_tags", boardId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kanban_board_tags")
        .select("*")
        .eq("board_id", boardId)
        .order("name");
        
      if (error) throw error;
      return data as KanbanBoardTag[];
    },
    enabled: isOpen
  });

  // Criar nova tag do quadro
  const createBoardTagMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const { data, error } = await supabase
        .from("kanban_board_tags")
        .insert({
          board_id: boardId,
          name: name.trim(),
          color
        })
        .select()
        .single();
        
      if (error) throw error;
      return data as KanbanBoardTag;
    },
    onSuccess: (newBoardTag) => {
      queryClient.invalidateQueries({ queryKey: ["board_tags", boardId] });
      // Adicionar automaticamente ao cartão
      addTagToCardMutation.mutate({
        name: newBoardTag.name,
        color: newBoardTag.color
      });
      setNewTagName("");
      setIsCreatingNewTag(false);
      toast.success("Tag criada e adicionada ao cartão!");
    },
    onError: (error: any) => {
      console.error("Erro ao criar tag do quadro:", error);
      if (error.code === '23505') {
        toast.error("Já existe uma tag com este nome neste quadro");
      } else {
        toast.error("Erro ao criar tag");
      }
    }
  });

  // Adicionar tag ao cartão
  const addTagToCardMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      const { data, error } = await supabase
        .from("kanban_card_tags")
        .insert({
          card_id: cardId,
          name,
          color
        })
        .select()
        .single();
        
      if (error) throw error;
      return data as KanbanCardTag;
    },
    onSuccess: (newCardTag) => {
      onTagAdded(newCardTag);
    },
    onError: (error: any) => {
      console.error("Erro ao adicionar tag ao cartão:", error);
      if (error.code === '23505') {
        toast.error("Esta tag já foi adicionada ao cartão");
      } else {
        toast.error("Erro ao adicionar tag ao cartão");
      }
    }
  });

  const handleCreateNewTag = () => {
    if (!newTagName.trim()) {
      toast.error("Nome da tag não pode ser vazio");
      return;
    }

    createBoardTagMutation.mutate({
      name: newTagName.trim(),
      color: selectedColor
    });
  };

  const handleSelectBoardTag = (boardTag: KanbanBoardTag) => {
    // Verificar se a tag já está no cartão
    const isAlreadySelected = selectedTags.some(tag => tag.name === boardTag.name);
    if (isAlreadySelected) {
      toast.error("Esta tag já foi adicionada ao cartão");
      return;
    }

    addTagToCardMutation.mutate({
      name: boardTag.name,
      color: boardTag.color
    });
  };

  const availableBoardTags = boardTags.filter(boardTag => 
    !selectedTags.some(selectedTag => selectedTag.name === boardTag.name)
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {selectedTags.map((tag) => (
          <Badge
            key={tag.id}
            style={{ backgroundColor: tag.color, color: 'white' }}
            className="px-2 py-1 text-xs font-normal flex items-center gap-1"
          >
            {tag.name}
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onTagRemoved(tag.id);
              }}
              className="p-0.5 hover:bg-white/10 rounded"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
      </div>
      
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-1 h-8">
            <Tag className="h-3.5 w-3.5" />
            <span>Adicionar Tag</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4">
          <div className="space-y-4">
            <h4 className="font-medium">Tags do Quadro</h4>
            
            {/* Tags existentes do quadro */}
            {availableBoardTags.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Tags disponíveis:</p>
                <div className="flex flex-wrap gap-1">
                  {availableBoardTags.map((boardTag) => (
                    <button
                      key={boardTag.id}
                      onClick={() => handleSelectBoardTag(boardTag)}
                      className="px-2 py-1 text-xs rounded-full text-white hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: boardTag.color }}
                    >
                      {boardTag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Criar nova tag */}
            <div className="border-t pt-3">
              {isCreatingNewTag ? (
                <div className="space-y-3">
                  <Input
                    placeholder="Nome da nova tag"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleCreateNewTag();
                      } else if (e.key === "Escape") {
                        setIsCreatingNewTag(false);
                        setNewTagName("");
                      }
                    }}
                    autoFocus
                  />
                  
                  <div>
                    <p className="text-sm mb-2">Cor</p>
                    <div className="grid grid-cols-5 gap-2">
                      {colors.map((color) => (
                        <button
                          key={color}
                          type="button"
                          style={{ backgroundColor: color }}
                          className="w-full aspect-square rounded-md flex items-center justify-center"
                          onClick={() => setSelectedColor(color)}
                        >
                          {color === selectedColor && (
                            <Check className="h-4 w-4 text-white" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      onClick={handleCreateNewTag}
                      disabled={!newTagName.trim() || createBoardTagMutation.isPending}
                      size="sm"
                    >
                      Criar
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => {
                        setIsCreatingNewTag(false);
                        setNewTagName("");
                      }}
                      size="sm"
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreatingNewTag(true)}
                  className="w-full flex items-center gap-2"
                  size="sm"
                >
                  <Plus className="h-4 w-4" />
                  Criar Nova Tag
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
